/**
 * I2 — Daewoo-style signup.
 *
 * Three changes from the legacy form:
 *   1. Email is split into a local part input + an @domain dropdown that pulls
 *      its options from /api/auth/directory. Admin maintains the allowed-domain
 *      list under Settings → Org Directory; the dropdown shows whatever is
 *      active. If admin hasn't added any domains, we fall back to a free-text
 *      email field (backward-compatible).
 *   2. Designation is a hard dropdown — system designations (BDA / BDE / BDM
 *      / Operations / Performance Marketing / Other) plus anything the admin
 *      added on top.
 *   3. Office branch is always a dropdown — from org directory, or a built-in
 *      list (Noida, Zirakpur, Chandigarh, Mumbai, Hyderabad) as fallback.
 *
 * The previous OTP flow is untouched. The backend now accepts both shapes —
 * the legacy single `email` field and the new `emailLocal + emailDomain`
 * pair — so this change is forward-compat with any other client still using
 * the old payload.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import AgeonLogo from '../components/AgeonLogo';
import EmailDomainField, { buildEmailAddress } from '../components/EmailDomainField';
import { useAuth, routeForStep } from '../store/auth';

const FALLBACK_OFFICES = [
    { slug: 'noida', label: 'Noida' },
    { slug: 'zirakpur', label: 'Zirakpur' },
    { slug: 'chandigarh', label: 'Chandigarh' },
    { slug: 'mumbai', label: 'Mumbai' },
    { slug: 'hyderabad', label: 'Hyderabad' },
];

export default function Signup() {
    const [form, setForm] = useState({
        name: '', email: '', emailLocal: '', emailDomain: '',
        phone: '', designation: '', salesDesignation: '',
        officeBranch: '', password: '', confirm: '',
    });
    const [dir, setDir] = useState(null);          // /api/auth/directory snapshot
    const [dirErr, setDirErr] = useState('');
    const signup = useAuth(s => s.signup);
    const loading = useAuth(s => s.loading);
    const nav = useNavigate();

    /* Pull the directory once on mount. Public endpoint — no token needed. */
    useEffect(() => {
        api.get('/auth/directory')
            .then(r => {
                setDir(r.data);
                const domains = r.data?.emailDomains || [];
                if (domains.length > 0) {
                    setForm(f => ({ ...f, emailDomain: f.emailDomain || domains[0].slug }));
                }
            })
            .catch(e => setDirErr(e?.response?.data?.error || 'Could not load signup options'));
    }, []);

    const enforceDomain = !!dir?.enforceEmailDomain;
    const domainOptions = dir?.emailDomains || [];
    const designations = dir?.designations || [];
    const offices = (dir?.offices?.length ? dir.offices : FALLBACK_OFFICES);

    async function submit(e) {
        e.preventDefault();
        if (form.password !== form.confirm) return toast.error('Passwords do not match');
        if (form.password.length < 8) return toast.error('Password must be at least 8 characters');
        const resolvedEmail = buildEmailAddress({
            email: form.email,
            emailLocal: form.emailLocal,
            emailDomain: form.emailDomain,
            enforceDomain,
        });
        if (!resolvedEmail) return toast.error('Enter your work email');
        if (form.phone && form.phone.length !== 10) return toast.error('Phone number must be exactly 10 digits');
        if (!form.salesDesignation) return toast.error('Pick your designation');
        if (!form.officeBranch) return toast.error('Pick your office branch');

        try {
            const payload = {
                name: form.name.trim(),
                phone: form.phone.trim(),
                designation: form.designation.trim(),
                salesDesignation: form.salesDesignation,
                officeBranch: form.officeBranch,
                password: form.password,
                role: 'sales_user',
                ...(enforceDomain
                    ? { emailLocal: form.emailLocal.trim(), emailDomain: form.emailDomain }
                    : { email: resolvedEmail }),
            };
            const res = await signup(payload);
            nav(routeForStep(res?.user));
        } catch { /* toasted */ }
    }

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-white">
            <div className="hidden lg:flex flex-col justify-between p-10 bg-gradient-to-br from-brand-teal to-brand-teal-700 text-white">
                <AgeonLogo variant="white" />
                <div>
                    <h1 className="text-4xl font-extrabold leading-tight max-w-md">Join the AGEON team.</h1>
                    <p className="mt-4 text-white/80 max-w-md">
                        Create your sales account. An admin will review &amp; approve before you can sign in.
                    </p>
                    <ul className="mt-6 space-y-2 text-sm text-white/80 max-w-md">
                        <li>• Use your work email address</li>
                        <li>• Pick your designation &amp; office branch</li>
                        <li>• Admin verifies before access is granted</li>
                    </ul>
                </div>
                <div className="text-xs text-white/70">© {new Date().getFullYear()} EBG x AGEON</div>
            </div>

            <div className="flex items-center justify-center p-6 lg:p-10">
                <form onSubmit={submit} className="w-full max-w-md space-y-4">
                    <div className="lg:hidden mb-6"><AgeonLogo /></div>
                    <h2 className="text-2xl font-bold">Create your account</h2>
                    <p className="text-sm text-ink-500">Sales user signup. Admin approval required.</p>

                    {dirErr && (
                        <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded p-2">
                            {dirErr}
                        </div>
                    )}

                    <Field label="Full name *" value={form.name} onChange={v => setForm({ ...form, name: v })} required />

                    <EmailDomainField
                        label="Work email"
                        enforceDomain={enforceDomain}
                        domainOptions={domainOptions}
                        email={form.email}
                        emailLocal={form.emailLocal}
                        emailDomain={form.emailDomain}
                        onChange={patch => setForm({ ...form, ...patch })}
                        required
                        placeholder="you"
                        hint={enforceDomain
                            ? `Pick your company domain: ${domainOptions.map(d => '@' + d.slug).join(', ')}`
                            : 'Any valid email address'}
                    />

                    {/* Designation — system + admin-added */}
                    <div>
                        <label className="ag-label">Designation *</label>
                        <select
                            className="ag-input"
                            value={form.salesDesignation}
                            onChange={e => setForm({ ...form, salesDesignation: e.target.value })}
                            required
                        >
                            <option value="">— pick your role —</option>
                            {designations.map(d => (
                                <option key={d.slug} value={d.slug}>{d.label}</option>
                            ))}
                        </select>
                        <div className="text-[11px] text-ink-500 mt-1">
                            Admin will confirm your role during approval.
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field
                            label="Phone"
                            type="tel"
                            value={form.phone}
                            onChange={v => setForm({ ...form, phone: v.replace(/\D/g, '').slice(0, 10) })}
                            placeholder="mobile number"
                           
                        />
                        <div>
                            <label className="ag-label">Office branch *</label>
                            <select
                                className="ag-input"
                                value={form.officeBranch}
                                onChange={e => setForm({ ...form, officeBranch: e.target.value })}
                                required
                            >
                                <option value="">— pick branch —</option>
                                {offices.map(o => (
                                    <option key={o.slug} value={o.label}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* <Field
                        label="Free-form title (optional)"
                        value={form.designation}
                        onChange={v => setForm({ ...form, designation: v })}
                        placeholder="e.g. Senior BDA — Delhi NCR"
                        hint="Display label shown alongside your designation."
                    /> */}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Password *" type="password" value={form.password} onChange={v => setForm({ ...form, password: v })} required hint="Min 8 characters" />
                        <Field label="Confirm password *" type="password" value={form.confirm} onChange={v => setForm({ ...form, confirm: v })} required />
                    </div>

                    <button className="ag-btn-primary w-full justify-center" disabled={loading || !dir}>
                        {loading ? 'Creating…' : 'Create account'}
                    </button>
                    <div className="text-center text-sm text-ink-500">
                        Already have an account? <Link to="/login" className="ag-link">Sign in</Link>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, type = 'text', required, hint, placeholder }) {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    const inputType = isPassword && showPassword ? 'text' : type;

    return (
        <div>
            <label className="ag-label">{label}</label>
            <div className="relative">
                <input
                    className={`ag-input ${isPassword ? 'pr-10' : ''}`}
                    type={inputType}
                    required={required}
                    value={value}
                    placeholder={placeholder}
                    autoComplete={isPassword ? 'new-password' : undefined}
                    onChange={e => onChange(e.target.value)}
                />
                {isPassword && (
                    <button
                        type="button"
                        onClick={() => setShowPassword(v => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-ink-500 hover:text-ink-800 hover:bg-ink-100"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                )}
            </div>
            {hint && <div className="text-[11px] text-ink-500 mt-1">{hint}</div>}
        </div>
    );
}
